import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const MasterSettingsModal = ({ isOpen, onClose, selectedGateway, onGatewayChange }) => {
  const [tempGateway, setTempGateway] = React.useState(selectedGateway);

  React.useEffect(() => {
    setTempGateway(selectedGateway);
  }, [selectedGateway, isOpen]);

  const gateways = [
    {
      id: 'SHOPII',
      name: 'Shopii Gates',
      subtitle: 'Hq Shopify Gateways',
      pricing: [
        { label: 'Live cards', cost: '-3 credits', color: 'text-green-400' },
        { label: 'Dead cards', cost: '-1 credit', color: 'text-red-400' },
        { label: 'Errors/Captcha', cost: 'Free', color: 'text-gray-400' },
      ],
    },
    {
      id: 'STRIPE',
      name: 'Stripe Gates',
      subtitle: 'Auth Stripe Gateways',
      pricing: [
        { label: 'Live cards', cost: '-2 credits', color: 'text-green-400' },
        { label: 'Dead cards', cost: '-1 credit', color: 'text-red-400' },
        { label: 'Errors/Captcha', cost: 'Free', color: 'text-gray-400' },
      ],
    },
    {
      id: 'PP',
      name: 'PP Gates',
      subtitle: 'PayPal Gateways',
      pricing: [
        { label: 'Live cards', cost: '-2.5 credits', color: 'text-green-400' },
        { label: 'Dead cards', cost: '-1 credit', color: 'text-red-400' },
        { label: 'Errors/Captcha', cost: 'Free', color: 'text-gray-400' },
      ],
    },
    {
      id: 'B3',
      name: 'B3 Gates',
      subtitle: 'Braintree Gateways',
      pricing: [
        { label: 'Live cards', cost: '-2 credits', color: 'text-green-400' },
        { label: 'Dead cards', cost: '-1 credit', color: 'text-red-400' },
        { label: 'Errors/Captcha', cost: 'Free', color: 'text-gray-400' },
      ],
    },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full max-w-md bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl border border-gray-700/50 shadow-2xl max-h-[85vh] overflow-y-auto">
              <div className="sticky top-0 bg-gradient-to-b from-gray-900 to-transparent p-6 pb-4 border-b border-gray-800/50 backdrop-blur-xl z-10">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 hover:bg-gray-800/50 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-400" />
                </button>
                <h2 className="text-2xl font-bold text-white mb-1">Master Settings</h2>
                <p className="text-gray-400 text-sm">Configure your checker settings and gateways</p>
              </div>

              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-white mb-4">Gateway Type</h3>
                  <div className="space-y-3">
                    {gateways.map((gateway, index) => (
                      <motion.button
                        key={gateway.id}
                        onClick={() => setTempGateway(gateway.id)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          tempGateway === gateway.id
                            ? 'border-green-500 bg-green-900/20'
                            : 'border-gray-700/50 bg-gray-800/30 hover:border-gray-600'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="text-white font-bold text-base">{gateway.name}</h4>
                            <p className="text-gray-400 text-sm">{gateway.subtitle}</p>
                          </div>
                          <motion.div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              selectedGateway === gateway.id
                                ? 'border-green-500 bg-green-500'
                                : 'border-gray-600'
                            }`}
                            animate={selectedGateway === gateway.id ? { scale: [1, 1.2, 1] } : {}}
                            transition={{ duration: 0.3 }}
                          >
                            {selectedGateway === gateway.id && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="w-2 h-2 bg-white rounded-full"
                              />
                            )}
                          </motion.div>
                        </div>
                        <div className="space-y-1">
                          <p className="text-purple-400 text-sm font-semibold mb-1">Pricing:</p>
                          {gateway.pricing.map((price, idx) => (
                            <p key={idx} className={`text-xs ${price.color}`}>
                              • {price.label}: {price.cost}
                            </p>
                          ))}
                        </div>
                      </motion.button>
                    ))}
                  </div>

                  {/* Save Button */}
                  <div className="mt-6">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        onGatewayChange(tempGateway);
                        onClose();
                      }}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-bold py-3 rounded-xl"
                    >
                      Save Gateway
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MasterSettingsModal;
